import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import EventEmitter from './EventEmitter';

/// <summary>
/// Resources class for loading various types of assets.
/// </summary>
/// <param name="sources">Array of sources to load, each with a type and path.</param>
/// <returns>Instance of Resources class that emits 'ready' event when all assets are loaded.</returns>
/// <example>
/// const resources = new Resources([
///     { name: 'model', type: 'gltfModel', path: '/models/model.glb' },
///     { name: 'texture', type: 'texture', path: '/textures/texture.jpg' },
///     { name: 'skybox', type: 'cubeTexture', path: ['/textures/px.jpg', '/textures/nx.jpg', '/textures/py.jpg', '/textures/ny.jpg', '/textures/pz.jpg', '/textures/nz.jpg'] }
/// ]);
/// resources.on('ready', () => {
///     console.log('All resources loaded:', resources.items);
/// });
export default class Resources extends EventEmitter
{
    constructor(sources) {
        super();
        this.sources = sources;
        this.items = {};
        this.toLoad = this.sources.length;
        this.loaded = 0;
        this.progress = 0;

        this.setLoader();
        this.startLoading();
    }

    setLoader() {
        //NOTE: 適したローダーを設定を必要に応じて追加
        this.loaders = {};
        this.loaders.gltfLoader = new GLTFLoader();
        this.loaders.textureLoader = new THREE.TextureLoader();
        this.loaders.cubeTextureLoader = new THREE.CubeTextureLoader();
        this.loaders.audioLoader = new THREE.AudioLoader();
        // 動画ローダーを追加
        this.loaders.videoLoader = this.createVideoLoader();
    }
    
    createVideoLoader() {
        return {
            load: (url, onLoad) => {
                const video = document.createElement('video');
                video.src = url;
                video.crossOrigin = 'anonymous';
                video.loop = true;
                video.muted = true;
                video.playsInline = true;
                // 'metadata' だと切替時の初回再生でバッファ待ちが起き得るため 'auto' で先読みする
                video.preload = 'auto';
                // 動画がロード可能になったらonLoadを呼び出す
                video.addEventListener('canplay', () => {
                    onLoad(video);
                }, { once: true });
                // エラーが発生した場合のハンドリング
                video.addEventListener('error', (e) => {
                    console.error(`Failed to load video: ${url}`, e);
                });

                // ロードを開始
                video.load();
            }
        };
    }

    startLoading() {
        for (const source of this.sources) {
            if (source.type === 'gltfModel') {
                this.loaders.gltfLoader.load(source.path, (file) => {
                    this.sourceLoaded(source, file);
                });
            } else if (source.type === 'texture') {
                this.loaders.textureLoader.load(source.path, (file) => {
                    this.sourceLoaded(source, file);
                });
            } else if (source.type === 'cubeTexture') {
                this.loaders.cubeTextureLoader.load(source.path, (file) => {
                    this.sourceLoaded(source, file);
                });
            } else if (source.type === 'audio') {
                this.loaders.audioLoader.load(source.path, (file) => {
                    this.sourceLoaded(source, file);
                });
            } else if (source.type === 'video') {
                this.loaders.videoLoader.load(source.path, (file) => {
                    this.sourceLoaded(source, file);
                });
            } else {
                console.warn(`Unknown source type: ${source.type}`);
            }
        }
    }

    sourceLoaded(source, file) {
        this.items[source.name] = file;
        this.loaded++;
        this.progress = this.loaded / this.toLoad;

        this.trigger('progress');

        if (this.loaded === this.toLoad) {
            // 全てのリソースが読み込まれたときの発火
            this.trigger('ready');
        }
    }
}